import {inject, Injectable, signal} from '@angular/core'
import {AuthService} from './auth-service'
import {forkJoin, map, Observable, of, switchMap, tap} from 'rxjs'
import {IGitLabAuth} from '../1-application/types'
import {HttpClient, HttpHeaders} from '@angular/common/http'
import {environment} from '../../environments/environment'
import {LOCAL_STORAGE} from '../0-general/providers'
import {GITLAB_TOKEN_NAME} from '../1-application/constants'
import {
  IGitLabCreateMRResult,
  IGitlabFileResult,
  IGitLabGroup,
  IGitLabProject,
  IGitLabSaveFileResult,
  IVersionFile,
  TVersion
} from './gitlab'

export const GITLAB_HOST = 'https://gitlab.com/api/v4'

const GROUPS_KEY = 'gitlabGroups'
const PROJECTS_KEY = 'gitlabProjects'

@Injectable({
  providedIn: 'root'
})
export class GitlabService {

  public projects = signal<IGitLabProject[]>([])
  public groups = signal<IGitLabGroup[]>([])

  private service = inject(AuthService)
  private storage = inject(LOCAL_STORAGE)

  private http = inject(HttpClient)


  public listGroups(update = false): Observable<IGitLabGroup[]> {
    const stored = this.storage.getItem(GROUPS_KEY)
    if (!update && stored) {
      return of(JSON.parse(stored) as IGitLabGroup[])
    }

    const groups: IGitLabGroup[] = []
    return forkJoin([
      this.listGroupsInternal(5335894),
      this.listGroupsInternal(61754934)
    ]).pipe(
      switchMap(res => {
        groups.push(...res[0], ...res[1])
        return forkJoin([
          this.getGroup(5335894),
          this.getGroup(61754934)
        ])
      }),
      map(res => {
        return res.concat(groups)
      }),
      tap(groups => {
        this.groups.set(groups)
        this.storage.setItem(GROUPS_KEY, JSON.stringify(groups))
      })
    )
  }


  public listProjects(update = false): Observable<IGitLabProject[]> {
    const q = new URLSearchParams({
      simple: 'true',
      membership: 'true',
      per_page: '100'
      //search: 'sparbankensyd'
    })
    const url = `${GITLAB_HOST}/projects?${q.toString()}` //${groupId}/projects?all_available=true`
    const f = this.setCredentials()
      .pipe(
        switchMap(res => {
          const headers = new HttpHeaders({'Authorization': `Bearer ${res.access_token}`})
          return this.getItems<IGitLabProject>(url, headers, [])
        })
      )
    return this.getWithCache<IGitLabProject[]>(PROJECTS_KEY, f, update)
      .pipe(
        tap(p => this.projects.set(p))
      )
  }

  /**
   *
   * @param projectId
   * @param from
   * @param to
   * @param assignee
   * @param squash
   */
  public createMR(projectId: number, from: string, to: string, assignee: number, squash: boolean): Observable<IGitLabCreateMRResult> {
    const body = {
      id: projectId,
      source_branch: from,
      target_branch: to,
      title: 'Automated MR',
      assignee_id: assignee,
      squash: squash
    }

    const url = `${GITLAB_HOST}/projects/${projectId}/merge_requests`
    return this.getCredentials().pipe(
      switchMap(headers => {
        return this.http.post<IGitLabCreateMRResult>(url, body, {headers})
      })
    )
  }

  public acceptMR(projectId: number, iid: number, squash: boolean) {

    const body = {
      id: projectId,
      merge_request_iid: iid,
      merge_when_pipeline_succeeds: true,
      squash: squash
    }

    const url = `${GITLAB_HOST}/projects/${projectId}/merge_requests/${iid}/merge`

    return this.getCredentials().pipe(
      switchMap(headers => {
        return this.http.put<any>(url, body, {headers})
      })
    )
  }


  public getFile(projectId: number, fileName: string, branch: string) {
    const url = `https://gitlab.com/api/v4/projects/${projectId}/repository/files/${encodeURIComponent(fileName)}?ref=${branch}`
    return this.getCredentials().pipe(
      switchMap(headers => {
        return this.http.get<IGitlabFileResult>(url, {headers})
      })
    )
  }

  public saveFile(projectId: number, content: any, fileName: string, branch: string) {
    const body = {
      file_path: encodeURIComponent(fileName),
      branch,
      encoding: 'base64',
      // Make sure it is pretty in the UI as well.
      content: globalThis.btoa(JSON.stringify(content, null, 2)),
      commit_message: 'Bot Update of file'
    }

    const url = `${GITLAB_HOST}/projects/${projectId}/repository/files/${encodeURIComponent(fileName)}`
    return this.getCredentials()
      .pipe(
        switchMap(headers => {
          return this.http.put<IGitLabSaveFileResult>(url, body, {headers})
        })
      )
  }

  /**
   * Rudimentary but working "semver", returns a new version.
   * @param file - Anything with a property 'version'
   * @param level MINOR | MAJOR | PATCH
   */
  public updateVersion(file: IVersionFile, level: TVersion) {
    const parts: string[] = file.version.split('.')
    let pos = 0 // Major

    if (level === 'MAJOR') {
      parts[1] = '0'
      parts[2] = '0'
    } else if (level === 'MINOR') {
      pos = 1
      parts[2] = '0'
    } else {
      pos = 2
    }
    parts[pos] = `${Number.parseInt(parts[pos]) + 1}`
    return parts.join('.')
  }


  /**
   * Refresh the access token, public so that we can invoke it
   * manually, just for testing.
   */
  public refresh(): Observable<any> {
    const token = this.service.gitLabToken().refresh_token!
    return this.http.get<IGitLabAuth>(`${environment.apiUrl}/refresh-gitlab?rt=${token}`).pipe(
      tap(res => {
        this.service.gitLabToken.set(res)
        this.storage.setItem(GITLAB_TOKEN_NAME, JSON.stringify(res))
      })
    )
  }

  private getGroup(id: number): Observable<IGitLabGroup> {
    const url = `${GITLAB_HOST}/groups/${id}`
    return this.getCredentials()
      .pipe(
        switchMap(headers => {
          return this.http.get<IGitLabGroup>(url, {headers})
        })
      )
  }

  private listGroupsInternal(id: number): Observable<IGitLabGroup[]> {

    const q = new URLSearchParams({
      per_page: '100'
    }).toString()

    const url = `${GITLAB_HOST}/groups/${id}/descendant_groups?${q}` //${groupId}/projects?all_available=true`
    return this.getCredentials()
      .pipe(
        switchMap(headers => {
          return this.http.get<IGitLabGroup[]>(url, {headers})
        })
      )
  }


  /**
   * Invokes and set the result in the "cache" (local storage). If not
   * explicitly force an update, returns the cached version.
   * @param key - The cache item key
   * @param func - The function to invoke, any observable is fine
   * @param update
   */
  private getWithCache = <T>(key: string, func: Observable<T>, update = false): Observable<T> => {
    const stored = this.storage.getItem(key)
    if (!update && stored) {
      return of(JSON.parse(stored) as T)
    }
    return func.pipe(
      tap(items => this.storage.setItem(key, JSON.stringify(items)))
    )
  }

  /**
   * Highly opportunistic
   *
   * @param url - The start URL, we will continue by nexts.
   * @param headers - The headers you want to _send_ (authorization)
   * @param items - An array that will be filled with items of type T.
   * @private
   */
  private getItems<T>(url: string, headers: HttpHeaders, items: T[]): Observable<T[]> {
    return this.http.get(url, {headers, observe: 'response'}).pipe(
      switchMap(res => {
        items.push(...(res.body as T[]))
        const next = this.resolveNext(res.headers)
        if (next) {
          return this.getItems(next, headers, items)
        }
        return of(items)
      })
    )
  }

  /**
   * Look in headers to see if this has continue or not.
   * @param headers
   * @private
   */
  private resolveNext(headers: HttpHeaders): string | undefined {
    if (!headers.has('link')) {
      return
    }

    const items = headers.get('link')!.split(',')
      .filter(i => i.includes('rel="next"'))
      .map(i => {
        const parts = i.split(';')
        return /<(.*)>/.exec(parts[0])![1]
      })
    return items[0]
  }

  private getCredentials(): Observable<HttpHeaders> {
    return this.setCredentials().pipe(
      map(res =>
        new HttpHeaders({'Authorization': `Bearer ${res.access_token}`}))
    )
  }

  /**
   * Supposed to check token validity
   * @private
   */
  private setCredentials(): Observable<Partial<IGitLabAuth>> {
    const token = this.service.gitLabToken()
    // If token expired refresh.
    return of(token)
  }
}
